import { getTokenCookie } from "./helper-data";

/*
 * Session cookies shared with the enterprise app on the parent domain.
 *
 * The auth service rotates the refresh token on every refresh and blacklists the old JTI,
 * which the access and refresh tokens of a session share. So whichever app refreshes must
 * publish the new pair here — the other app's copy becomes revoked, not merely expired, and
 * cannot be refreshed a second time.
 *
 * On plain localhost, use domain=localhost so both apps share cookies across ports.
 * On `.localhost` (E2E Docker), domain cookies are rejected — enterprise uses query-param handoff.
 */

const ACCESS_TOKEN_COOKIE = `${process.env.REACT_APP_ENV}_auth_token`;
const REFRESH_TOKEN_COOKIE = `${process.env.REACT_APP_ENV}_refresh_token`;
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken";

// Refresh tokens live for 7 days; keep the cookie alive at least that long.
const SESSION_COOKIE_DAYS = 7;

export const getAccessToken = () =>
  getTokenCookie(ACCESS_TOKEN_COOKIE) || localStorage.getItem("token");

export const getRefreshToken = () =>
  getTokenCookie(REFRESH_TOKEN_COOKIE) ||
  localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

// Returns JWT `iat` (seconds) or null when the token is unreadable.
export const readJwtIssuedAt = (token) => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload?.iat === "number" ? payload.iat : null;
  } catch {
    return null;
  }
};

// True only when `candidate` was issued after `existing`.
export const isAccessTokenStrictlyNewer = (candidate, existing) => {
  const candidateIat = readJwtIssuedAt(candidate);
  if (candidateIat == null) return false;
  if (!existing) return true;
  const existingIat = readJwtIssuedAt(existing);
  if (existingIat == null) return false;
  return candidateIat > existingIat;
};

// Adopt a pair the enterprise app (or another tab) published to shared cookies.
// Requires both cookies and an access token strictly newer than the stale one.
export const tryAdoptPublishedSession = (staleAccessToken) => {
  if (!staleAccessToken) return null;

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (
    !accessToken ||
    !refreshToken ||
    !isAccessTokenStrictlyNewer(accessToken, staleAccessToken)
  ) {
    return null;
  }

  localStorage.setItem("token", accessToken);
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  return accessToken;
};

export const persistSession = (accessToken, refreshToken) => {
  if (accessToken) {
    writeSessionCookie(ACCESS_TOKEN_COOKIE, accessToken);
    localStorage.setItem("token", accessToken);
  }

  const nextRefreshToken = refreshToken || getRefreshToken();
  if (nextRefreshToken) {
    writeSessionCookie(REFRESH_TOKEN_COOKIE, nextRefreshToken);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, nextRefreshToken);
  }
};

export const clearSession = () => {
  deleteSessionCookie(ACCESS_TOKEN_COOKIE);
  deleteSessionCookie(REFRESH_TOKEN_COOKIE);
  localStorage.removeItem("token");
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
};

const resolveCookieDomain = () => {
  const configured = process.env.REACT_APP_COOKIE_DOMAIN;
  if (configured) {
    return configured;
  }
  // Share cookies across localhost ports (enterprise-web :4200 + career-portal :3000).
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return "localhost";
  }
  return null;
};

const cookieDomainAttribute = () => {
  const domain = resolveCookieDomain();
  return domain ? ` domain=${domain};` : "";
};

// `secure; samesite=none` is required for cross-subdomain sharing, but only valid over https.
const cookieSecurityAttributes = () =>
  window.location.protocol === "https:" ? " secure; samesite=none;" : "";

const clearHostOnlySessionCookie = (name) => {
  const expires = "Thu, 01 Jan 1970 00:00:00 UTC";
  document.cookie = `${name}=; expires=${expires}; path=/;${cookieSecurityAttributes()}`;
};

const writeSessionCookie = (name, value) => {
  clearHostOnlySessionCookie(name);
  const expires = new Date(
    Date.now() + SESSION_COOKIE_DAYS * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie =
    `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/;` +
    cookieDomainAttribute() +
    cookieSecurityAttributes();
};

const deleteSessionCookie = (name) => {
  const expires = "Thu, 01 Jan 1970 00:00:00 UTC";
  const security = cookieSecurityAttributes();
  document.cookie =
    `${name}=; expires=${expires}; path=/;` +
    cookieDomainAttribute() +
    security;
  // Host-only duplicate from an older handoff or pre-domain write.
  document.cookie = `${name}=; expires=${expires}; path=/;${security}`;
};
